import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  BookOpen, CalendarClock, Receipt, FolderOpen, Link as LinkIcon, LifeBuoy,
  Mail as MailIcon, ExternalLink, Award, ClipboardList, Wallet, FileText,
} from 'lucide-react';
import PortalTopBar from '../../components/academy/PortalTopBar';
import PortalSidebar from '../../components/academy/PortalSidebar';
import AppTile from '../../components/academy/AppTile';
import { useAuth } from '../../context/AuthContext';

/* GCU-style "My Apps" oval-tile dashboard, grouped into subtle categories so the
 * page reads as 4 quick lanes instead of one wall of icons. Categories are
 * intentionally short — each only carries the tiles a student naturally clusters
 * mentally (Academics, Records, Finance, Help). */
const APP_GROUPS = [
  {
    id: 'academics',
    label: 'Academics',
    apps: [
      { to: '/academy/courses',     icon: BookOpen,      label: 'My Courses',          testId: 'app-tile-courses' },
      { to: '/academy/schedule',    icon: CalendarClock, label: 'Class Schedule / Grades', testId: 'app-tile-schedule' },
      { to: '/academy/surveys',     icon: ClipboardList, label: 'My Surveys',          testId: 'app-tile-surveys' },
    ],
  },
  {
    id: 'records',
    label: 'Records',
    apps: [
      { to: '/academy/certificates',icon: Award,         label: 'My Certificates',     testId: 'app-tile-certificates' },
      { to: '/academy/transcripts', icon: FileText,      label: 'Transcript Request',  testId: 'app-tile-transcripts' },
      { to: '/academy/documents',   icon: FolderOpen,    label: 'Document Management', testId: 'app-tile-documents' },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    apps: [
      { to: '/academy/finance',     icon: Wallet,        label: 'Finance Hub',         testId: 'app-tile-finance' },
      { to: '/academy/invoices',    icon: Receipt,       label: 'Invoices',            testId: 'app-tile-invoices' },
    ],
  },
  {
    id: 'help',
    label: 'Help',
    apps: [
      { to: '/academy/links',       icon: LinkIcon,      label: 'Helpful Links',       testId: 'app-tile-links' },
      { to: '/academy/support',     icon: LifeBuoy,      label: 'Technical Support',   testId: 'app-tile-support' },
    ],
  },
];
const TOTAL_APPS = APP_GROUPS.reduce((acc, g) => acc + g.apps.length, 0);

const PORTAL_LABEL = {
  student: 'STUDENT ACCESS',
  parent: 'PARENT ACCESS',
  instructor: 'FACULTY ACCESS',
  admin: 'ADMIN ACCESS',
};

export default function Dashboard() {
  const { user, api } = useAuth();
  const [courses, setCourses] = useState([]);
  const [m365, setM365] = useState([]);

  useEffect(() => {
    let active = true;
    api.get('/api/academy/my/courses').then(({ data }) => {
      if (active) setCourses(data.rows || []);
    }).catch(() => {});
    api.get('/api/academy/me/m365-access').then(({ data }) => {
      if (active) setM365(data.rows || []);
    }).catch(() => {});
    return () => { active = false; };
  }, [api]);

  // Route by role: admin → unified /admin panel (Academy tab) where they
  // manage programs, users, enrollments, audit log etc.
  if (user?.role === 'admin') return <Navigate to="/admin?tab=academy" replace />;
  if (user?.role === 'parent') return <Navigate to="/academy/parent" replace />;
  if (user?.role === 'instructor') return <Navigate to="/academy/instructor" replace />;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <PortalTopBar portalLabel={PORTAL_LABEL[user?.role] || 'PORTAL'} />
      <main className="max-w-[1440px] mx-auto px-4 lg:px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          <PortalSidebar user={user} courses={courses} />

          <div className="flex-1 min-w-0">
            <div className="rounded-2xl overflow-hidden border border-[#E5E5E7]">
              <div className="bg-[#0e0e10] text-white px-6 py-3.5 flex items-center justify-between">
                <span className="text-[14px] font-bold tracking-[0.18em] uppercase">My Apps</span>
                <span className="text-[12px] text-white/60">{TOTAL_APPS} apps</span>
              </div>
              <div className="bg-white p-5 sm:p-7 space-y-7">
                {APP_GROUPS.map((g) => (
                  <section key={g.id} data-testid={`app-group-${g.id}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-[#a8871a]">{g.label}</span>
                      <span className="flex-1 h-px bg-gradient-to-r from-[#c9a227]/30 via-[#E5E5E7] to-transparent" />
                      <span className="text-[10px] tabular-nums text-[#9aa0a6]">{g.apps.length}</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                      {g.apps.map((a) => <AppTile key={a.label} {...a} />)}
                    </div>
                  </section>
                ))}
              </div>
            </div>

            {/* M365 Access tile — visible only when an admin has provisioned access */}
            {m365.length > 0 && (
              <div className="mt-8 rounded-2xl overflow-hidden border border-[#E5E5E7]">
                <div className="bg-[#0e0e10] text-white px-6 py-3.5 flex items-center justify-between">
                  <span className="text-[14px] font-bold tracking-[0.18em] uppercase">Microsoft 365 access</span>
                  <span className="text-[12px] text-white/60">{m365.length} grant{m365.length === 1 ? '' : 's'}</span>
                </div>
                <div className="bg-white p-5 space-y-5">
                  {m365.map((g) => (
                    <div key={`${g.user_id}-${g.program_slug}`}
                      data-testid={`m365-grant-${g.program_slug}`}
                      className="rounded-xl border border-[#E5E5E7] p-4">
                      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                        <div className="min-w-0">
                          <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#a8871a]">For program</div>
                          <div className="text-[14px] font-extrabold text-[#1D1D1F]">{g.program_slug}</div>
                        </div>
                        <a
                          href={`mailto:${g.m365_email}`}
                          className="inline-flex items-center gap-1.5 text-[12.5px] font-mono text-[#1D1D1F] bg-[#FAFAFA] border border-[#E5E5E7] rounded-md px-2.5 py-1.5 hover:border-[#c9a227]/60"
                        >
                          <MailIcon className="w-3.5 h-3.5" /> {g.m365_email}
                        </a>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-6 gap-2">
                        {Object.entries(g.app_urls || {}).map(([app, url]) => (
                          <a key={app} href={url} target="_blank" rel="noopener noreferrer"
                            data-testid={`m365-launch-${app}`}
                            className="group inline-flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-[#E5E5E7] hover:border-[#c9a227] hover:bg-[#fff8e7] transition-colors">
                            <span className="text-[12.5px] font-bold capitalize">{app}</span>
                            <ExternalLink className="w-3.5 h-3.5 text-[#6E6E73] group-hover:text-[#a8871a]" />
                          </a>
                        ))}
                      </div>
                      {g.notes && <div className="mt-3 text-[11.5px] text-[#6E6E73]">📝 {g.notes}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Active courses summary */}
            <div className="mt-8 rounded-2xl overflow-hidden border border-[#E5E5E7]">
              <div className="bg-[#0e0e10] text-white px-6 py-3.5">
                <span className="text-[14px] font-bold tracking-[0.18em] uppercase">Active courses</span>
              </div>
              <div className="bg-white p-5">
                {courses.length === 0 ? (
                  <p className="text-[14px] text-[#6E6E73] py-2 px-2">
                    You're not enrolled in any course yet.{' '}
                    <a href="/academy#programs" className="text-[#c9a227] font-semibold hover:underline">Browse programs</a>
                  </p>
                ) : (
                  <ul className="divide-y divide-[#E5E5E7]">
                    {courses.map((c) => (
                      <li key={c.program_slug} className="py-3 flex items-center justify-between gap-3">
                        <div>
                          <div className="font-bold text-[#1D1D1F]">{c.program_name}</div>
                          <div className="text-[12.5px] text-[#6E6E73]">
                            {c.tier_id} · {c.schedule || 'Self-paced'}
                          </div>
                        </div>
                        <span className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-amber-50 text-[#a8871a] border border-amber-200">
                          {c.status}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
